import { useToast } from '@chakra-ui/react'
import { createContext, FC, useState } from 'react'

import followUpFornecedorService from '@service/followUpFornecedor'

import {
  IConfigCompradorContext,
  IConfigCompradorItem,
  IConfigCompradorPayload,
  IResponsavelApiItem
} from '@base/@types/providers/FollowUpFornecedor/ConfigCompradorProvider'

export const ConfigCompradorContext = createContext<IConfigCompradorContext>(
  {} as IConfigCompradorContext
)

// Groups flat API items into grouped frontend model
function groupResponsaveis(
  items: IResponsavelApiItem[]
): IConfigCompradorItem[] {
  const map = new Map<string, IConfigCompradorItem>()
  items.forEach(item => {
    const key = `${item.supplierId}_${item.compactCategoryId ?? 'null'}`
    if (!map.has(key)) {
      map.set(key, {
        id: key,
        fornecedorId: item.supplierId,
        fornecedorNome:
          item.supplierDescription ?? `Fornecedor ${item.supplierId}`,
        categoriaCompactaId: item.compactCategoryId,
        categoriaCompactaNome: item.compactCategoryDescription,
        usuarios: []
      })
    }
    map.get(key)!.usuarios.push(item.userEmail)
  })
  return Array.from(map.values())
}

const ConfigCompradorProvider: FC = ({ children }) => {
  const toast = useToast()
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<IConfigCompradorItem[]>([])

  const getData = async () => {
    setLoading(true)
    try {
      const { data: res } = await followUpFornecedorService.getResponsaveis()
      setData(groupResponsaveis(res.data ?? []))
    } catch {
      toast({
        title: 'Erro ao carregar responsáveis',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'bottom-right'
      })
    } finally {
      setLoading(false)
    }
  }

  const create = async (payload: IConfigCompradorPayload) => {
    // Flatten: one entry per (category × user) combination
    const itens = payload.categoriaCompactaIds.flatMap(catId =>
      payload.usuarios.map(email => ({
        supplierId: payload.fornecedorId,
        compactCategoryId: catId,
        userEmail: email
      }))
    )
    try {
      await followUpFornecedorService.createResponsaveis({ itens })
      toast({
        title: 'Configuração criada com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right'
      })
      await getData()
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? 'Erro ao criar configuração'
      toast({
        title: msg,
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'bottom-right'
      })
    }
  }

  const update = async (id: string, payload: IConfigCompradorPayload) => {
    // Find the row being updated to get supplierId
    const existing = data.find(d => d.id === id)
    if (!existing) return

    const supplierId = existing.fornecedorId

    // Get all OTHER rows for this supplier (to re-add after delete)
    const otherRows = data.filter(
      d => d.fornecedorId === supplierId && d.id !== id
    )

    try {
      // DELETE all for this supplier
      await followUpFornecedorService.deleteResponsaveis(supplierId)

      // Re-add all other rows for this supplier
      const readdItens = otherRows.flatMap(row =>
        row.usuarios.map(email => ({
          supplierId: row.fornecedorId,
          compactCategoryId: row.categoriaCompactaId,
          userEmail: email
        }))
      )

      // Add the updated row (update always targets a single category)
      const newItens = payload.categoriaCompactaIds.flatMap(catId =>
        payload.usuarios.map(email => ({
          supplierId: payload.fornecedorId,
          compactCategoryId: catId,
          userEmail: email
        }))
      )

      const allItens = [...readdItens, ...newItens]
      if (allItens.length > 0) {
        await followUpFornecedorService.createResponsaveis({ itens: allItens })
      }

      toast({
        title: 'Configuração atualizada com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right'
      })
      await getData()
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ?? 'Erro ao atualizar configuração'
      toast({
        title: msg,
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'bottom-right'
      })
    }
  }

  const remove = async (id: string) => {
    const existing = data.find(d => d.id === id)
    if (!existing) return

    const supplierId = existing.fornecedorId

    // Get all OTHER rows for this supplier (to re-add after delete)
    const otherRows = data.filter(
      d => d.fornecedorId === supplierId && d.id !== id
    )

    try {
      // DELETE all for this supplier
      await followUpFornecedorService.deleteResponsaveis(supplierId)

      // Re-add the other rows if any
      if (otherRows.length > 0) {
        const readdItens = otherRows.flatMap(row =>
          row.usuarios.map(email => ({
            supplierId: row.fornecedorId,
            compactCategoryId: row.categoriaCompactaId,
            userEmail: email
          }))
        )
        await followUpFornecedorService.createResponsaveis({
          itens: readdItens
        })
      }

      toast({
        title: 'Responsável removido',
        status: 'info',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right'
      })
      await getData()
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? 'Erro ao remover responsável'
      toast({
        title: msg,
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'bottom-right'
      })
    }
  }

  return (
    <ConfigCompradorContext.Provider
      value={{ loading, data, getData, create, update, remove }}
    >
      {children}
    </ConfigCompradorContext.Provider>
  )
}

export default ConfigCompradorProvider
